import React from 'react';
import { Drawer, Spin, Empty, Select, Button, Space, Collapse } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import ResponsiveECharts from '../../components/ResponsiveECharts';
import { METRIC_COLOR, MUTED_COLOR } from '../../components/metricColors';
import { buildCurveOption } from './curveOption';
import CurveTable from './CurveTable';
import useMaskCloseGuard from '../../components/useMaskCloseGuard';
import { FONT } from "../../styles/typography";

/**
 * 訂房曲線 (Pickup) 抽屜。三個入口共用：前瞻圖點柱、定價清單點日期、房型維度點近期進單。
 *
 * 純呈現 —— 區間、房型選擇與資料載入都留在 OpsDashboard，因為那三個入口各自要用
 * 儀錶板的區間語彙 (檢視期間 / 前瞻桶 / 單日) 才算得出 from-to。這裡只負責畫。
 */

/** 常駐說明只留「怎麼下判斷」這一句，其餘收進可收合區塊。 */
function Guidance() {
  return (
    <div style={{ marginBottom: 8 }}>
      今年線落在<span style={{ color: METRIC_COLOR.occ }}>淺藍帶</span>內＝進度正常，
      之上＝超前 (可考慮拉價)、之下＝落後 (需刺激需求)；
      決策看<span style={{ color: METRIC_COLOR.revpar }}>紫線 RevPAR</span> (右軸)。
    </div>
  );
}

/** 圖例與欄位定義。預設收起 —— 熟了之後每次都要跳過它才看得到圖。 */
function LegendPanel() {
  return (
    <Collapse ghost className="curve-legend">
      <Collapse.Panel header={<span style={{  }}>看懂這張圖與下方表格</span>} key="legend">
        <ul style={{ color: '#595959', paddingLeft: 18, margin: 0, lineHeight: 1.9 }}>
          <li>X 軸：左＝離入住越早，右＝入住當日。</li>
          <li><span style={{ color: METRIC_COLOR.occ }}>藍</span>＝住房率 (實線今年、虛線去年)；
            <span style={{ color: METRIC_COLOR.revpar }}>紫</span>＝RevPAR (右軸)；
            <span style={{ color: METRIC_COLOR.adr }}>綠</span>＝ADR，預設隱藏，點圖例開啟。</li>
          <li>淺藍帶＝近 5 年同提前天數的 P25–P75；灰虛線＝近年中位；
            <span style={{ color: '#fa541c' }}>紅虛線</span>＝現在。</li>
          <li>下方表格逐提前天數列出 在手／ADR／RevPAR／營收，上為今年、
            <span style={{ color: MUTED_COLOR }}>下為去年</span>。</li>
          <li>「較前點 RevPAR%」＝該段 RevPAR 變動率，並拆成 ADR 與量的貢獻
            (%ΔRevPAR ≈ %ΔADR + %Δ量)，用來看調價有沒有真的推升每房產值。</li>
        </ul>
      </Collapse.Panel>
    </Collapse>
  );
}

export default function CurveDrawer({
  open, stay, curveData, curLead, loading,
  room, roomTypes, onRoomChange, onOpenOrders, onClose, onStep, canStep,
}) {
  const hasData = curveData && curveData.length > 0;
  // 開啟動畫期間遮罩是暴露的，行動裝置遲到的相容性點擊會打在上面把抽屜關掉
  const maskClosable = useMaskCloseGuard(open);
  return (
    // 控制項放內容區而非標題列 —— 標題只留「這張圖在講哪一段」，多塞一個下拉與一顆按鈕
    // 在抽屜寬度下必定斷行，標題斷成兩行會把整個內容往下推
    <Drawer title={
      <Space size={4}>
        {/* 「這天低，旁邊幾天呢？」是單日診斷的第一個追問。沒有這兩顆，回答它只能關掉抽屜、
            回到 90 根柱裡重新找那一天 —— 比較三天就要走三趟完整流程 */}
        <Button size="small" type="text" icon={<LeftOutlined aria-hidden />} disabled={!canStep || loading}
          onClick={() => onStep && onStep(-1)} aria-label="看前一日的訂房曲線"
          title={canStep ? '前一日' : '只有單日曲線可以逐日切換'} />
        <span>訂房曲線 (Pickup) · {stay || ''}</span>
        <Button size="small" type="text" icon={<RightOutlined aria-hidden />} disabled={!canStep || loading}
          onClick={() => onStep && onStep(1)} aria-label="看後一日的訂房曲線"
          title={canStep ? '後一日' : '只有單日曲線可以逐日切換'} />
      </Space>
    } placement="right" width={760}
      visible={open} onClose={onClose} maskClosable={maskClosable}>
      <Spin spinning={loading}>
        {/* 控制列：左邊選看哪個房型、右邊跳到該區間的在手訂單。
            無資料時也要在 —— 那正是想換個房型再看一次的時候 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, marginBottom: 12, paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
          <Space size={6}>
            <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>房型</span>
            {/* 在手與分母 (該房型房數) 由後端一併切換 */}
            <Select size="small" aria-label="房型" style={{ width: 180 }} value={room || ''}
              onChange={onRoomChange} disabled={loading}>
              <Select.Option value="">全館</Select.Option>
              {(roomTypes || []).map((r) => {
                const cod = String(r.room_cod).trim();
                return <Select.Option key={cod} value={cod}>{r.room_nam || cod}</Select.Option>;
              })}
            </Select>
          </Space>
          <Button size="small" onClick={onOpenOrders}>目前在手訂單</Button>
        </div>
        {hasData ? (
          <>
            <Guidance />
            <ResponsiveECharts option={buildCurveOption(curveData)} style={{ height: 340 }} />
            <LegendPanel />
            <CurveTable curveData={curveData} curLead={curLead} />
          </>
        ) : (!loading && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="此期間無足夠歷史資料" />)}
      </Spin>
    </Drawer>
  );
}
